import type { ValueBase } from "@react-types/shared";
import {
  type FieldPath,
  type FieldValues,
  type RegisterOptions,
  useForm,
  type UseFormProps,
  type UseFormRegister,
  type UseFormRegisterReturn,
  type UseFormReturn,
} from "react-hook-form";

/**
 * Used to make the onChange callback compatible with react-aria wich doesn't send an event but the value of the field
 */
export function useRegisterReactAria<
  TFieldValues extends FieldValues,
  TFieldName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
  register: UseFormRegister<TFieldValues>,
): (
  name: TFieldName,
  options?: RegisterOptions<TFieldValues, TFieldName>,
) => Omit<UseFormRegisterReturn<TFieldName>, "onChange"> & Pick<ValueBase<unknown>, "onChange"> {
  return (name, options) => {
    const { onChange, ...rest } = register(name, options);

    return {
      ...rest,
      onChange: (value: unknown) => {
        // We need to expose the name too to make the formState working, like trigger "isDirty", ...
        void onChange({ target: { value, name } });
      },
    };
  };
}

/**
 * Used to make the onChange callback compatible with react-aria. @see useRegisterReactAria
 * The rest of the react-hook-form useForm() function is simply exposed.
 */
export function useFormAria<
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues extends FieldValues | undefined = undefined,
>(
  props?: UseFormProps<TFieldValues, TContext>,
): Omit<UseFormReturn<TFieldValues, TContext, TTransformedValues>, "register"> & {
  register: ReturnType<typeof useRegisterReactAria<TFieldValues>>;
} {
  const { register: _register, ...rest } = useForm(props);
  const register = useRegisterReactAria(_register);

  return { ...rest, register };
}

export type FormAriaHook<
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues extends FieldValues | undefined = undefined,
> = ReturnType<typeof useFormAria<TFieldValues, TContext, TTransformedValues>>;
